import {
	listNotifications,
	getUnreadNotificationCount,
	markNotificationRead,
	markAllNotificationsRead,
	deleteNotification,
} from "#lib/apis/notifications-api.js";
import type { Notification } from "#lib/api-client/types.gen.js";
import { presentNotification } from "#lib/realtime/notification-toast.js";

/** How many notifications the bell keeps in memory. */
const MAX_ITEMS = 30;

/**
 * Notification state for the bell and the live WebSocket feed.
 *
 * The list is hydrated exactly once per app session (on page load or login) and
 * kept current by live `notification.created` frames after that. There is no
 * polling: the only reads of the notifications API happen on hydration and when
 * the user acts on a notification.
 */
class NotificationsStore {
	items = $state<Notification[]>([]);
	unreadCount = $state(0);
	loading = $state(false);
	/** True after the first successful hydration; prevents re-fetching on route changes. */
	hydrated = $state(false);
	#hydrating: Promise<void> | null = null;
	#resyncing: Promise<void> | null = null;

	/** Loads the initial notifications list and unread count once per session. */
	async hydrate(): Promise<void> {
		if (this.hydrated || this.#hydrating) return this.#hydrating ?? undefined;
		this.loading = true;
		this.#hydrating = this.#fetch()
			.catch((error: unknown) => {
				console.error("Failed to load notifications:", error);
			})
			.finally(() => {
				this.loading = false;
				this.#hydrating = null;
			});
		return this.#hydrating;
	}

	/**
	 * Reconciles with the API after a WebSocket reconnect. Frames that arrived
	 * while the socket was down were never delivered, so this is the one time the
	 * list is re-read outside hydration/login: it catches up any notification
	 * missed in the gap without introducing polling. Deliberately silent (no
	 * spinner, no toasts) because the user did not just do anything.
	 */
	async resync(): Promise<void> {
		if (this.#resyncing) return this.#resyncing;
		this.#resyncing = this.#fetch()
			.catch((error: unknown) => {
				console.error("Failed to resync notifications:", error);
			})
			.finally(() => {
				this.#resyncing = null;
			});
		return this.#resyncing;
	}

	/** Fetches the list and unread count and replaces local state. */
	async #fetch(): Promise<void> {
		const [response, unread] = await Promise.all([
			listNotifications(MAX_ITEMS),
			getUnreadNotificationCount(),
		]);
		this.items = response.items;
		this.unreadCount = unread;
		this.hydrated = true;
	}

	/**
	 * Applies a live notification: prepends it to the bell, updates the unread
	 * badge and presents it (toast/sound) according to its delivery level.
	 */
	receive(notification: Notification, unreadCount?: number): void {
		if (!notification?.id) return;
		this.items = [notification, ...this.items.filter((item) => item.id !== notification.id)].slice(
			0,
			MAX_ITEMS,
		);
		if (typeof unreadCount === "number") {
			this.unreadCount = unreadCount;
		} else if (!notification.is_read) {
			this.unreadCount += 1;
		}
		presentNotification(notification);
	}

	/** Marks one notification read, updating the badge optimistically. */
	async markRead(id: string): Promise<void> {
		const notification = this.items.find((item) => item.id === id);
		if (!notification || notification.is_read) return;
		try {
			await markNotificationRead(id);
			this.items = this.items.map((item) =>
				item.id === id ? { ...item, is_read: true, read_at: new Date().toISOString() } : item,
			);
			this.unreadCount = Math.max(0, this.unreadCount - 1);
		} catch (error) {
			console.error("Failed to mark notification as read:", error);
		}
	}

	/** Marks every notification read. */
	async markAllRead(): Promise<void> {
		try {
			await markAllNotificationsRead();
			this.items = this.items.map((item) => ({ ...item, is_read: true }));
			this.unreadCount = 0;
		} catch (error) {
			console.error("Failed to mark all notifications as read:", error);
		}
	}

	/** Removes a notification from the bell. */
	async remove(id: string): Promise<void> {
		const notification = this.items.find((item) => item.id === id);
		try {
			await deleteNotification(id);
			this.items = this.items.filter((item) => item.id !== id);
			if (notification && !notification.is_read) {
				this.unreadCount = Math.max(0, this.unreadCount - 1);
			}
		} catch (error) {
			console.error("Failed to delete notification:", error);
		}
	}

	/** Resets state on logout. */
	reset(): void {
		this.items = [];
		this.unreadCount = 0;
		this.hydrated = false;
		this.loading = false;
		this.#hydrating = null;
		this.#resyncing = null;
	}
}

export const notifications = new NotificationsStore();
